// Werks-Auswahl (Lager-WerkDropdown-Idiom, PM-eigen): IMMER sichtbar — zeigt das aktive Werk als
// harten Scope; aufklappbar nur, wenn das Profil mehr als ein Werk erlaubt (fail-closed, die
// Optionen kommen ausschliesslich aus scope.plants). Sitzt rechts oben auf dem Home und in den
// Anlege-Wizards (alles, was ein Werk in den SAP-Payload schreibt).
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

/**
 * @param {object} me — traegt profile.scope.plants + activePlant
 * @param {Function} onPlantChange — (plant: string) => void, hebt die Auswahl in die Modul-Shell
 */
function PlantSelect({ me, onPlantChange }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const plants = me.profile.scope.plants;
  const multi = plants.length > 1;
  // Anzeigename je Werk (Demo: aus dem Profil; echte Quelle spaeter SAP T001W NAME1 via
  // Adapter/Cache — die Werksliste ist Berater-Input, sap-contract-requirements.md §6g).
  // Fallback auf den Code, wenn kein Name gepflegt ist (fremdes Customizing).
  const names = me.profile.scope.plantNames || {};
  const nameOf = (p) => names[p] || p;

  // Klick ausserhalb schliesst das Menue (wie WerkDropdown im Lager).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => multi && setOpen((v) => !v)}
        aria-haspopup={multi ? 'listbox' : undefined}
        aria-expanded={multi ? open : undefined}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition-all ${isDarkMode
          ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12]'
          : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40'
        } ${multi ? '' : 'cursor-default'}`}
      >
        <span className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          {t('pm.order.fieldPlant')}:
        </span>
        {/* Zu: der NAME (wie im Lager); Code + Name stehen im aufgeklappten Menue. */}
        <span className="truncate max-w-[140px]">{nameOf(me.activePlant)}</span>
        {multi && (
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''} ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
        )}
      </button>

      {open && multi && (
        <div className={`absolute right-0 top-full mt-1 z-40 min-w-[200px] rounded-xl border shadow-lg overflow-hidden ${isDarkMode
          ? 'bg-gray-900 border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.5)]'
          : 'bg-white border-slate-200'
        }`}>
          {plants.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => { setOpen(false); onPlantChange(p); }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-3 ${p === me.activePlant
                ? (isDarkMode ? 'bg-brand/20 text-blue-200' : 'bg-brand/[0.08] text-brand')
                : (isDarkMode ? 'text-white/80 hover:bg-white/[0.08]' : 'text-slate-700 hover:bg-slate-50')
              }`}
            >
              <span className={`font-mono text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{p}</span>
              <span className="truncate">{nameOf(p)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default PlantSelect;
